import { formatDistanceToNow } from 'date-fns';
import {
  AlertTriangle,
  CheckCircle2,
  CircleDashed,
  Clock,
  Loader2,
  RefreshCw,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import { canResync, type EhrSyncState } from '@/lib/ehr-sync-state';

interface EhrSyncStatusProps {
  state: EhrSyncState;
  /** Offered next to a failed or retrying sync; omit to show the status only. */
  onResync?: () => void;
  isResyncing?: boolean;
  className?: string;
}

function ago(iso: string): string {
  return formatDistanceToNow(new Date(iso), { addSuffix: true });
}

interface Display {
  icon: React.ElementType;
  iconClass: string;
  label: string;
  /** Tooltip lines; none means no tooltip. */
  details: string[];
}

function displayFor(state: EhrSyncState): Display {
  switch (state.kind) {
    case 'synced':
      return {
        icon: CheckCircle2,
        iconClass: 'text-green-600 dark:text-green-500',
        label: state.syncedAt ? `Synced ${ago(state.syncedAt)}` : 'Synced with EHR',
        details: state.syncedAt ? [formatDateTime(state.syncedAt)] : [],
      };
    case 'syncing':
      return {
        icon: Loader2,
        iconClass: 'text-amber-600 dark:text-amber-500 animate-spin',
        label: 'Syncing with EHR…',
        details: [],
      };
    case 'queued':
      // Static on purpose: nothing says the push has picked it up (ADR-292).
      return {
        icon: Clock,
        iconClass: 'text-amber-600 dark:text-amber-500',
        label: 'Queued for EHR',
        details: ['Waiting to be sent to the EHR. It is sent within minutes.'],
      };
    case 'retrying':
      return {
        icon: AlertTriangle,
        iconClass: 'text-amber-600 dark:text-amber-500',
        label: 'EHR sync delayed, retrying',
        details: [
          ...(state.error ? [state.error] : []),
          state.lastSyncedAt
            ? `Last synced ${formatDateTime(state.lastSyncedAt)}`
            : 'Not synced with the EHR yet',
        ],
      };
    case 'failed':
      return {
        icon: XCircle,
        iconClass: 'text-red-600 dark:text-red-500',
        label: state.failedAt ? `Sync failed ${ago(state.failedAt)}` : 'EHR sync failed',
        details: [
          ...(state.error ? [state.error] : []),
          state.lastSyncedAt
            ? `Last successful sync ${formatDateTime(state.lastSyncedAt)}`
            : 'Never synced successfully',
        ],
      };
    case 'never':
      return {
        icon: CircleDashed,
        iconClass: 'text-gray-400 dark:text-gray-500',
        label: 'Not synced yet',
        details: [],
      };
  }
}

/**
 * When a record last synced with the EHR, or that it failed, with a Re-sync
 * button beside a failure (ADR-284). Safe inside a clickable row: its own clicks
 * and key presses don't reach the row.
 */
export function EhrSyncStatus({
  state,
  onResync,
  isResyncing = false,
  className,
}: EhrSyncStatusProps): React.ReactElement {
  const display = displayFor(state);
  const Icon = display.icon;
  const stop = (e: React.SyntheticEvent): void => e.stopPropagation();

  const label = (
    <span className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
      <Icon className={cn('h-4 w-4 shrink-0', display.iconClass)} />
      {display.label}
    </span>
  );

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      {display.details.length > 0 ? (
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0} className="cursor-default" onClick={stop} onKeyDown={stop}>
                {label}
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs space-y-1">
              {display.details.map((line) => (
                <p key={line}>{line}</p>
              ))}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        label
      )}
      {onResync && canResync(state) && (
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-2 text-xs"
          disabled={isResyncing}
          onClick={(e) => {
            e.stopPropagation();
            onResync();
          }}
          onKeyDown={stop}
        >
          <RefreshCw className={cn('mr-1.5 h-3.5 w-3.5', isResyncing && 'animate-spin')} />
          {state.kind === 'retrying' ? 'Retry now' : 'Re-sync'}
        </Button>
      )}
    </div>
  );
}
